import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm, useWatch } from 'react-hook-form';
import { theme } from '~/theme';

import HelpIcon from '@mui/icons-material/Help';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import { Checkbox, Input } from '~/components/Common/Forms/fields';
import DebugModal from '~/components/DebugModal/DebugModal';
import PromoFailures from '~/components/Promo/PromoFailure/PromoFailures';
import PromoRequests from '~/components/Promo/PromoRequests/PromoRequests';
import PromoLogsContainer from '~/components/PromoV3/PromoLogs';

import { usePromoHeal } from '~/hooks/usePromoHeal';

import { getOrderDetail } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import currencyFormatter, { formatCurrency } from '~/utils/currencyFormatter';
import { getVoucherIdsFromPromoPaymentResponse } from '~/utils/getVoucherIdsFromPromoPaymentResponse';
import { getAllItemsMap, getBookingNumber, getItemDescription } from '~/utils/order';

import Spinner from '../../Common/Spinner';
import PromoLink from '../PromoLink';
import usePromoMeta from '../hooks/usePromoMeta';
import usePromoSimulator from '../hooks/usePromoSimulator';
import { PromoTesterUrlState } from '../hooks/usePromoTesterUrl';
import { promoCodePath } from '../promoUrls';

import Internals from './Internals';
import { parsePromoTesterVoucherIds } from './parseVoucherIds';

interface Props {
  initCodeName?: string;
  initOrderId?: string;
  /** Comma- or whitespace-separated voucher UUIDs (query string `voucherIds`) */
  initVoucherIdsRaw?: string;
  initApplyWithOrignalUserAccount?: boolean;
  initIgnoreLuxPlusPricing?: boolean;
  tenant: App.Tenant;
  /** Fired when the order’s customer user id is resolved (or cleared) for syncing with /my-vouchers tester */
  onOrderCustomerUserIdChange?: (userId: string | undefined) => void;
  // URL management props
  urlState?: PromoTesterUrlState;
  updateUrlState?: (newState: Partial<PromoTesterUrlState>) => void;
}

function formatWarningSuffix(warning: string | null | undefined, warningCode: string | null | undefined): string {
  const warningParts = [warning, warningCode].filter((value) => Boolean(value && value.trim()));
  return warningParts.length > 0 ? ` ${warningParts.join(' ')}` : '';
}

function getOrderVoucherIds(order: App.Order | null): Array<string> {
  if (!order?.payments?.length) {
    return [];
  }

  return Array.from(
    new Set(
      order.payments.flatMap((payment) =>
        getVoucherIdsFromPromoPaymentResponse(payment.response_json as Record<string, unknown> | null | undefined),
      ),
    ),
  );
}

function formatAmount(amount: number | null | undefined, currencyCode: string, regionCode: string): string {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '-';
  }

  return formatCurrency(amount, currencyCode, regionCode);
}

function formatChannelMarkupIds(item: App.AnyItem): string {
  const channelMarkupIds = [
    (item as { channel_markup_id?: string | null }).channel_markup_id,
    (item as { channelMarkup_ids?: Array<string | null> | null }).channelMarkup_ids,
    (item as { channelMarkupId?: string | null }).channelMarkupId,
    (item as { channelMarkupIds?: Array<string | null> | null }).channelMarkupIds,
  ]
    .flatMap((value) => {
      if (Array.isArray(value)) {
        return value;
      }
      return value ? [value] : [];
    })
    .filter((value): value is string => Boolean(value && value.trim()));

  return channelMarkupIds.length > 0 ? Array.from(new Set(channelMarkupIds)).join(', ') : '-';
}

function PromoTester({
  initCodeName,
  initOrderId,
  initVoucherIdsRaw,
  initApplyWithOrignalUserAccount,
  initIgnoreLuxPlusPricing,
  tenant,
  onOrderCustomerUserIdChange,
  // URL management props
  urlState,
  updateUrlState,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [fullOrder, setFullOrder] = useState<App.Order | null>(null);
  const [allItemsMap, setAllItemsMap] = useState<Record<string, App.AnyItem>>({});
  const [user, setUser] = useState<App.User | null>(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);
  const [orderLoadError, setOrderLoadError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<Array<{ title: string; message: string }>>();

  const initVoucherIds = useMemo(() => parsePromoTesterVoucherIds(initVoucherIdsRaw), [initVoucherIdsRaw]);

  const { orderDiscount, promoCodeErrors, isLoading, reSimulateDiscountEvent, internals } = usePromoSimulator({
    initOrderId,
    initCodeName,
    initApplyWithOrignalUserAccount,
    initIgnoreLuxPlusPricing,
    initVoucherIds,
  });

  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      codeName: initCodeName || '',
      orderId: initOrderId || '',
      voucherIds: initVoucherIdsRaw || '',
      brand: tenant.brand,
      applyWithOrignalUserAccount: initApplyWithOrignalUserAccount,
      ignoreLuxPlusPricing: initIgnoreLuxPlusPricing,
    },
  });

  const [watchOrderId, watchCodeName, watchVoucherIds, watchApplyOrig, watchIgnoreLux] = useWatch({
    control,
    name: ['orderId', 'codeName', 'voucherIds', 'applyWithOrignalUserAccount', 'ignoreLuxPlusPricing'],
  });
  const activeCodeName = watchCodeName?.trim() || initCodeName;

  useEffect(() => {
    if (!updateUrlState) {
      return;
    }
    updateUrlState({
      orderId: watchOrderId?.trim() || undefined,
      codeName: watchCodeName?.trim() || undefined,
      voucherIds: watchVoucherIds?.trim() || undefined,
      applyWithOrignalUserAccount: watchApplyOrig,
      ignoreLuxPlusPricing: watchIgnoreLux,
    });
  }, [updateUrlState, watchOrderId, watchCodeName, watchVoucherIds, watchApplyOrig, watchIgnoreLux]);

  useEffect(() => {
    setValue('codeName', initCodeName || '');
    setValue('orderId', initOrderId || '');
    setValue('voucherIds', initVoucherIdsRaw || '');
  }, [initCodeName, initOrderId, initVoucherIdsRaw, setValue]);

  // Fetch order and user data when orderId changes
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!initOrderId) {
        setFullOrder(null);
        setAllItemsMap({});
        setUser(null);
        setOrderLoadError(null);
        return;
      }

      setIsLoadingOrderData(true);
      setOrderLoadError(null);
      try {
        const orderData = await getOrderDetail(initOrderId);
        setFullOrder(orderData);

        // Create comprehensive items map using the same pattern as AccountingMetaTable
        const itemsMap = getAllItemsMap(orderData);
        setAllItemsMap(itemsMap);

        const orderVoucherIds = getOrderVoucherIds(orderData);
        const mergedVoucherIds = Array.from(
          new Set([...(parsePromoTesterVoucherIds(initVoucherIdsRaw ?? '') ?? []), ...orderVoucherIds]),
        ).join(', ');
        if (mergedVoucherIds) {
          setValue('voucherIds', mergedVoucherIds);
        }

        const effectiveCodeName = orderData.promo_code || initCodeName;
        if (orderData.promo_code && orderData.promo_code.length > 0) {
          setValue('codeName', orderData.promo_code);
        }
        if (effectiveCodeName) {
          reSimulateDiscountEvent({
            codeName: effectiveCodeName,
            orderId: initOrderId,
            brand: tenant.brand,
            applyWithOrignalUserAccount: initApplyWithOrignalUserAccount ?? false,
            ignoreLuxPlusPricing: initIgnoreLuxPlusPricing ?? true,
            voucherIds: Array.from(
              new Set([...(parsePromoTesterVoucherIds(initVoucherIdsRaw) ?? []), ...orderVoucherIds]),
            ),
          });
        }
        if (orderData?.fk_customer_id) {
          const userData = await UsersService.getUser(orderData.fk_customer_id);
          setUser(userData);
        }
      } catch (err) {
        console.error('Error fetching order data:', err);
        setOrderLoadError(`Failed to load order ${initOrderId}: ${err.message}`);
        // Don't show snackbar error - just set the warning state
      } finally {
        setIsLoadingOrderData(false);
      }
    };

    fetchOrderData();
  }, [
    initOrderId,
    initCodeName,
    initVoucherIdsRaw,
    initApplyWithOrignalUserAccount,
    initIgnoreLuxPlusPricing,
    enqueueSnackbar,
    reSimulateDiscountEvent,
    setValue,
    tenant.brand,
  ]);

  useEffect(() => {
    onOrderCustomerUserIdChange?.(user?.id_member ?? undefined);
  }, [user, onOrderCustomerUserIdChange]);

  // Update warnings when allItemsMap changes
  useEffect(() => {
    if (fullOrder && Object.keys(allItemsMap).length > 0) {
      setWarnings(getWarnings(fullOrder, allItemsMap));
    }
  }, [fullOrder, allItemsMap]);

  const setCode = (code) => {
    setValue('codeName', code);
    const values = getValues();
    reSimulateDiscountEvent({
      codeName: code,
      ...values,
      voucherIds: parsePromoTesterVoucherIds(values.voucherIds),
    });
  };

  const setOrder = (order) => {
    setValue('orderId', order);
    const values = getValues();
    reSimulateDiscountEvent({
      orderId: order,
      ...values,
      voucherIds: parsePromoTesterVoucherIds(values.voucherIds),
    });
  };

  const onSubmit = async (formData) => {
    if (formData.orderId === '') {
      enqueueSnackbar('Please enter an Order ID', { variant: 'error' });
      return;
    }
    try {
      reSimulateDiscountEvent({
        codeName: formData.codeName,
        orderId: formData.orderId,
        brand: tenant.brand,
        applyWithOrignalUserAccount: formData.applyWithOrignalUserAccount,
        ignoreLuxPlusPricing: formData.ignoreLuxPlusPricing,
        voucherIds: parsePromoTesterVoucherIds(formData.voucherIds),
      });
    } catch (err) {
      enqueueSnackbar(err.message || 'Error! Unable to update filters.', { variant: 'error' });
    }
  };

  type DiscountMismatch = {
    totalDiscountAmount: number;
    promoDiscountTotal: number;
    numberOfPromoPayments: number;
    numberOfPayments: number;
    difference: number;
  };

  // Check for discount amount mismatches
  const checkDiscountAmountMismatch = (
    order: App.Order,
    allItemsMap: Record<string, App.AnyItem>,
  ): DiscountMismatch => {
    if (!allItemsMap || !order) {
      return null;
    }

    const totalDiscountAmount = Object.values(allItemsMap).reduce(
      (sum, item) => sum + (item.promo_code_discount_amount || 0),
      0,
    );
    const promoDiscountTotal = order.payments.filter((p) => p.type === 'promo').reduce((sum, p) => sum + p.total, 0);

    if (Math.abs(totalDiscountAmount - promoDiscountTotal) > 0.01) {
      return {
        totalDiscountAmount,
        promoDiscountTotal,
        numberOfPromoPayments: order.payments.filter((p) => p.type === 'promo').length,
        numberOfPayments: order.payments.length,
        difference: Math.abs(totalDiscountAmount - promoDiscountTotal),
      };
    }
    return null;
  };

  const getWarnings = (
    order: App.Order,
    allItemsMap: Record<string, App.AnyItem>,
  ): Array<{ title: string; message: string }> => {
    const discountMismatch = checkDiscountAmountMismatch(order, allItemsMap);
    if (discountMismatch && discountMismatch.numberOfPayments > 0) {
      return [
        {
          title: 'Discount Amount Mismatch Detected!',
          message: `Total item discount amounts (${discountMismatch.totalDiscountAmount}) do not equal promo discount total (${discountMismatch.promoDiscountTotal}). Difference: ${discountMismatch.difference}`,
        },
      ];
    }
    return [];
  };

  const { healPromo, isHealing } = usePromoHeal();
  const promoPayments = fullOrder?.payments.filter((p) => p.type === 'promo');
  const nonPromoPayments = fullOrder?.payments.filter((p) => p.type !== 'promo');
  const totalItemPromoDiscount = Object.values(allItemsMap).reduce(
    (sum, item) => sum + (item?.promo_code_discount_amount || 0),
    0,
  );
  const totalPromoPaymentAmount = promoPayments?.reduce((sum, payment) => sum + Number(payment.amount || 0), 0) || 0;
  const promoTotalsDiffer = Math.abs(totalItemPromoDiscount - totalPromoPaymentAmount) > 0.01;
  const submittedCodeName = watchCodeName?.trim() || initCodeName;
  const submittedVoucherIds = parsePromoTesterVoucherIds(watchVoucherIds || initVoucherIdsRaw) ?? [];

  if (isPromoMetaLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Spinner />
        <Typography variant="body1" fontSize={14}>
          Loading promo meta...
        </Typography>
      </Stack>
    );
  }

  const urlBrand = urlState?.brand?.trim();
  const brandMismatch = !!urlBrand && urlBrand.toLowerCase() !== tenant.brand.toLowerCase();

  return (
    <>
      {brandMismatch && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            Wrong tenant for this link
          </Typography>
          <Typography variant="body2">
            The URL targets brand <strong>{urlBrand}</strong>, but you are in admin as <strong>{tenant.title}</strong> (
            {tenant.brand}). Switch tenant in the header to match before running simulate, or results may not reflect
            that brand.
          </Typography>
        </Alert>
      )}
      <Box mb={2} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="body1" fontSize={14}>
          This form allows for testing promo code against orders to allow for a shorter feedback loop when testing promo
          logic. It works best when using against orders that don't have any existing promo codes applied.
          <br />
          Note: This is testing the promo log behavior and not the promo code request behavior (which depends on the
          information be passed to /api/promo/discount when the promo is being retrieved - see{' '}
          <a href="/marketing/promo-requests">promoRequests</a> for more info here)
        </Typography>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Input fullWidth control={control} muiLabel="Order ID" name="orderId" />
            <Button onClick={() => setOrder('test')}>Set test order</Button>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Input fullWidth control={control} muiLabel="Code Name" name="codeName" />
            <Button onClick={() => setCode('e2etestall')}>Set e2etestall Promo</Button>
            <Button onClick={() => setCode('e2etest')}>Set e2etest Promo</Button>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Input
              fullWidth
              control={control}
              muiLabel="Voucher IDs (optional)"
              name="voucherIds"
              rows={3}
              helperText="Comma- or whitespace-separated UUIDs; sent as voucherIds to the discount calculator"
            />
            <Tooltip title="Matches the voucherIds query parameter on GET /api/promo/discount/orderId/:orderId (passed into the DiscountCalculator).">
              <IconButton sx={{ mt: 1 }}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={2}>
              <Checkbox control={control} label="Apply With Original Account" name="applyWithOrignalUserAccount" />
              <Tooltip title="Check this box to run the calculation with the original user's account (by default the calculation is run with the current user account)">
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
              <Checkbox control={control} label="Ignore LuxPlus Pricing" name="ignoreLuxPlusPricing" />
              <Tooltip title="By Default, LuxPlus Pricing is ignored when logging the promo (i.e. when we normally run this calculation logic). This is to reduce the rate of hard promo failures due to missing/inaccurate original package Price differences when running the calculation on the client-side and re-running the order-based calculation. This flag bypasses this default and includes LuxPlus Pricing when running these (promo log) calculations.">
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Button variant="contained" type="submit">
              Simulate for {tenant.title}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Order Load Error Warning */}
      {orderLoadError && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            Order Load Warning
          </Typography>
          <Typography variant="body2">{orderLoadError}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The promo calculation will still run, but order details may be incomplete.
          </Typography>
        </Alert>
      )}

      {/* Discount Amount Mismatch Warning */}
      {warnings?.length > 0 &&
        warnings.map((warning) => (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Discount Amount Mismatch Detected!
            </Typography>
            <Typography variant="body2">{warning.message}</Typography>
            <DebugModal title="Payments" type="generic" data={fullOrder?.payments} />
            <DebugModal title="All Items" type="generic" data={allItemsMap} />
          </Alert>
        ))}

      <Box>
        <Typography variant="h6" gutterBottom>
          Simulated Order Discount
        </Typography>
        {(submittedCodeName || submittedVoucherIds.length > 0) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Submitted inputs
            </Typography>
            {submittedCodeName && (
              <Typography variant="body2" gutterBottom>
                Applied promo code:{' '}
                <Link href={promoCodePath(submittedCodeName)} target="_blank" rel="noreferrer">
                  {submittedCodeName}
                </Link>
              </Typography>
            )}
            {submittedVoucherIds.length > 0 && (
              <Typography variant="body2">
                Vouchers:{' '}
                {submittedVoucherIds.map((voucherId, index) => (
                  <React.Fragment key={voucherId}>
                    {index > 0 ? ', ' : ''}
                    <Link href={`/marketing/vouchers/${voucherId}`} target="_blank" rel="noreferrer">
                      {voucherId}
                    </Link>
                  </React.Fragment>
                ))}
              </Typography>
            )}
          </Box>
        )}
        {isLoading && <Spinner />}

        {orderDiscount && (
          <>
            {orderDiscount.promo.items?.some((i) => (i.vouchersApplied?.length ?? 0) > 0) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Voucher breakdown
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Stack>
                    <DebugModal
                      title={`${'voucherTotal' in orderDiscount.promo ? orderDiscount.promo.voucherTotal : ''} ${
                        orderDiscount.promo.currency
                      } - Vouchers`}
                      type="generic"
                      data={orderDiscount.promo}
                    />
                    {orderDiscount.promo.items.map((item) => {
                      const vouchers = item.vouchersApplied ?? [];
                      if (vouchers.length === 0) {
                        return null;
                      }
                      return (
                        <DebugModal
                          key={`voucher-item-${item.itemId}`}
                          title={`${item.categoryBK} - ${item.itemId} - ${
                            vouchers.length
                          } voucher(s)${formatWarningSuffix(item.warning, item.warningCode)}`}
                          type="generic"
                          data={item}
                        />
                      );
                    })}
                  </Stack>
                  <Stack>
                    {orderDiscount.promo.items.flatMap((item) =>
                      (item.vouchersApplied ?? []).map((voucher, index) => (
                        <DebugModal
                          key={`${item.itemId}-${voucher.voucherId}-${index}`}
                          title={`${item.categoryBK} - ${
                            voucher.voucherName || voucher.voucherTitle || voucher.voucherId
                          } - ${voucher.amount} ${voucher.voucherCurrency || orderDiscount.promo.currency}`}
                          type="generic"
                          data={voucher}
                        />
                      )),
                    )}
                  </Stack>
                  <DebugModal title="Voucher all details" type="generic" data={orderDiscount} />
                </Stack>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Vouchers (discount calculator)
              </Typography>
              {'voucherTotal' in orderDiscount.promo && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Voucher total: {currencyFormatter(orderDiscount.promo.currency, orderDiscount.promo.voucherTotal)}
                </Typography>
              )}
              {orderDiscount.promo.items?.some((i) => (i.vouchersApplied?.length ?? 0) > 0) ? (
                orderDiscount.promo.items.map((item) => {
                  const vouchers = item.vouchersApplied ?? [];
                  if (vouchers.length === 0) {
                    return null;
                  }
                  return (
                    <Box key={item.itemId} sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.categoryBK} — {item.itemId}
                      </Typography>
                      <Stack spacing={0.5} sx={{ pl: 1 }}>
                        {vouchers.map((v, index) => (
                          <Box key={`${v.voucherId}-${index}`}>
                            <Typography variant="body2">
                              <Link href={`/marketing/vouchers/${v.voucherId}`} target="_blank" rel="noreferrer">
                                {v.voucherName || v.voucherTitle || v.voucherId}
                              </Link>
                              {' — '}
                              {currencyFormatter(v.voucherCurrency || orderDiscount.promo.currency, v.amount)}{' '}
                              (remaining{' '}
                              {currencyFormatter(
                                v.voucherCurrency || orderDiscount.promo.currency,
                                v.remainingAmountAfter,
                              )}
                              )
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No vouchersApplied on line items for this run.
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={3} alignItems="center">
              <Internals internals={orderDiscount.internals} />

              <Stack>
                <DebugModal
                  title={`${'discount_total' in orderDiscount.promo ? orderDiscount.promo.discount_total : ''} ${
                    orderDiscount.promo.currency
                  } - Promo Code ${orderDiscount.promo.code_name} `}
                  type="generic"
                  data={orderDiscount.promo}
                />
                {orderDiscount.promo.items.map((item) => {
                  return (
                    <DebugModal
                      key={item.itemId}
                      title={`${item.categoryBK} - ${item?.discountAmount ? `${item?.discountAmount}/` : ''}${
                        item.discountableTotal
                      } - [${item.discountValue}${item.discountType == 'percentage' ? '%' : ''}]${formatWarningSuffix(
                        item.warning,
                        item.warningCode,
                      )}`}
                      type="generic"
                      data={item}
                    />
                  );
                })}
              </Stack>
              <DebugModal title="All details" type="generic" data={orderDiscount} />
            </Stack>
          </>
        )}
        {internals && <Internals internals={internals} />}
        {promoCodeErrors?.length > 0 &&
          promoCodeErrors.map((pc, i) => <ErrorDisplay key={i} severity="warning" message={JSON.stringify(pc)} />)}
      </Box>

      <Divider />
      {!!activeCodeName && (
        <Box mt={4}>
          <PromoLogsContainer
            promo={{
              code_name: activeCodeName,
              promo_type: 'unknown',
            }}
            userIdFilter={user?.id_member}
            meta={promoMeta}
            defaultExpanded
            initLogAfter={urlState?.logAfter}
            initLogBefore={urlState?.logBefore}
          />
        </Box>
      )}
      <Divider />
      {user && (
        <Box mt={4}>
          {fullOrder && (
            <Stack alignItems="flex-start">
              <Typography variant="h6" gutterBottom>
                Order {formatCurrency(fullOrder.total, fullOrder.currency_code, fullOrder.region_code)}
              </Typography>
              <DebugModal title="Full Order" type="generic" data={fullOrder} />
              <Typography variant="h6" gutterBottom>
                Items ({Object.keys(allItemsMap).length})
              </Typography>
              {fullOrder && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Discount summary
                  </Typography>
                  <Typography variant="body2">
                    Item-level promo discount total:{' '}
                    <strong>
                      {formatAmount(totalItemPromoDiscount, fullOrder.currency_code, fullOrder.region_code)}
                    </strong>
                  </Typography>
                  {promoTotalsDiffer && (
                    <Typography variant="body2">
                      Promo payment discount total:{' '}
                      <strong>
                        {formatAmount(totalPromoPaymentAmount, fullOrder.currency_code, fullOrder.region_code)}
                      </strong>
                    </Typography>
                  )}
                </Box>
              )}
              {Object.entries(allItemsMap).map(([itemId, item]) => {
                const typedItem = item;
                const isCustomOffer = 'fk_custom_offer_id' in typedItem;
                const itemType = isCustomOffer ? 'custom_offer' : typedItem.type;
                const itemDescription = getItemDescription(typedItem, itemType);
                const bookingNumber = getBookingNumber(typedItem);
                const promoDiscount = typedItem?.promo_code_discount_amount || 0;
                const voucherAmount = typedItem?.voucher_discount_amount ?? 0;
                const channelMarkupIds = formatChannelMarkupIds(typedItem);
                const itemDiscountLabel = formatAmount(promoDiscount, fullOrder.currency_code, fullOrder.region_code);
                const voucherAmountLabel = formatAmount(voucherAmount, fullOrder.currency_code, fullOrder.region_code);

                return (
                  <Box key={itemId} mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      {itemDescription} -{' '}
                      {formatCurrency(typedItem.total, fullOrder.currency_code, fullOrder.region_code)}
                      {promoDiscount > 0 && (
                        <Box component="span" sx={{ color: 'success.main' }}>
                          {` [Item discount: ${itemDiscountLabel}]`}
                        </Box>
                      )}
                    </Typography>
                    {bookingNumber && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Booking: {bookingNumber}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type: {itemType} | ID: {itemId}
                    </Typography>
                    {promoDiscount > 0 && (
                      <Typography variant="body2" color="success.main" gutterBottom>
                        Item-level promo discount amount: {itemDiscountLabel}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Item-level voucher amount: {voucherAmountLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Channel markup ids: {channelMarkupIds}
                    </Typography>
                    <DebugModal
                      title={`ITEM: ${itemDescription} - ${formatCurrency(
                        typedItem.total,
                        fullOrder.currency_code,
                        fullOrder.region_code,
                      )}`}
                      type="generic"
                      data={typedItem}
                    />
                  </Box>
                );
              })}
              <Box>
                <Button
                  href={fullOrder.origin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                >
                  Restore cart
                </Button>
                <Button
                  href={`/purchases/${fullOrder.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                >
                  View order
                </Button>
                {orderDiscount?.promo?.parent_id_promo_code && (
                  <PromoLink promoId={orderDiscount.promo.parent_id_promo_code} title="parent promo" />
                )}
                {orderDiscount?.promo?.code_name && <PromoLink codeName={orderDiscount.promo.code_name} />}
              </Box>
              <Typography variant="h6" gutterBottom>
                Payments
              </Typography>
              {promoPayments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Promo Payments {promoPayments.length}
                  </Typography>
                  {promoPayments.map((p) => (
                    <>
                      <DebugModal key={p.id} title={`PAYMENT: ${p.type} - ${p.amount}`} type="generic" data={p} />
                      {p.response_json !== null && (
                        <>
                          {p.response_json?.promoItemInfo && (
                            <DebugModal
                              key={p.id}
                              title={`Promo Item Info (${p.response_json.promoItemInfo.length})`}
                              type="generic"
                              data={p.response_json.promoItemInfo}
                            />
                          )}
                          {p.response_json?.promoLoyaltyItemInfo && (
                            <DebugModal
                              key={p.id}
                              title={`Promo Loyalty Item Info (${p.response_json.promoLoyaltyItemInfo.length})`}
                              type="generic"
                              data={p.response_json.promoLoyaltyItemInfo}
                            />
                          )}
                        </>
                      )}
                    </>
                  ))}
                </Box>
              )}

              {nonPromoPayments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Non-Promo Payments {nonPromoPayments.length}
                  </Typography>
                  {nonPromoPayments &&
                    nonPromoPayments.length > 0 &&
                    nonPromoPayments.map((p) => (
                      <DebugModal key={p.id} title={`PAYMENT: ${p.type} - ${p.amount}`} type="generic" data={p} />
                    ))}
                </Box>
              )}

              {fullOrder.payment_plans?.plans?.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Payment Plans {fullOrder.payment_plans.plans.length}
                  </Typography>
                  <Stack alignItems="flex-start">
                    {fullOrder.payment_plans.plans.map((plan, i) => (
                      <DebugModal
                        key={`${plan.type}-${i}`}
                        title={`${plan.type} - ${plan.amount} - ${plan.status}`}
                        type="generic"
                        data={plan}
                      />
                    ))}
                  </Stack>
                  {Array.isArray(fullOrder?.payment_plans?.error_logs) &&
                    fullOrder?.payment_plans?.error_logs?.map((error, i) => (
                      <DebugModal
                        key={`${error.type}-${i}`}
                        title={`ERROR: ${error.type} - ${error.message}`}
                        type="generic"
                        data={error}
                      />
                    ))}
                  {fullOrder?.payment_plans?.error_logs && !Array.isArray(fullOrder?.payment_plans?.error_logs) && (
                    <DebugModal
                      title="payment_plans?.error_logs"
                      type="generic"
                      data={fullOrder?.payment_plans?.error_logs}
                    />
                  )}
                </Box>
              )}
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary" mb={2}>
            User:{' '}
            <Link href={`/users/${user.id_member}`} target="_blank" rel="noopener noreferrer">
              {user.fullName || `${user.givenName} ${user.surname}`}
            </Link>{' '}
            ({user.email})
          </Typography>

          <Divider />

          {isLoadingOrderData ? (
            <Spinner />
          ) : (
            <Stack spacing={0}>
              <PromoRequests
                initUserId={user.id_member}
                initCodeName={initCodeName}
                initPage={0}
                initLimit={10}
                brand={tenant.brand}
                promoMeta={promoMeta}
                defaultExpanded
                isPromoMetaLoading={isPromoMetaLoading}
              />

              <PromoFailures
                initUserId={user.id_member}
                initCodeName={initCodeName}
                brand={tenant.brand}
                promoMeta={promoMeta}
                initPage={0}
                initLimit={10}
                defaultExpanded
              />
            </Stack>
          )}
          <Divider />
          <Accordion>
            <AccordionSummary
              sx={{
                backgroundColor: theme.palette.grey[200],
              }}
            >
              <Typography>Actions</Typography>
            </AccordionSummary>
            {orderDiscount && (
              <AccordionDetails>
                <Button
                  onClick={() => healPromo({ targetPromoCodeName: orderDiscount.promo.code_name })}
                  variant="contained"
                  color="primary"
                >
                  Heal Promo Condition discount ids
                </Button>
              </AccordionDetails>
            )}
          </Accordion>
        </Box>
      )}
    </>
  );
}

export default PromoTester;
